import { c as _c } from "react/compiler-runtime";
import { useFormatter, useTranslations } from 'next-intl';
import { OutcomeThumb } from '@/components/market/resolved-panel/other-outcomes-parts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/app-table';
import { cn } from '@/lib/ui/utils';
import { isLopsidedShare } from './concentration-warning';
import { durationUnit } from './duration-unit';
import type { ComparisonMetric, IntegrityComparison, IntegrityOutcome } from './resolution-integrity-types';
import { useMetricFormat } from './use-metric-format';
import { WarningMarker } from './warning-marker';
interface TableRowSpec {
  key: string;
  label: string;
  /** Second line under the label, e.g. "% of outcome holdings". */
  hint?: string;
  /** Winner cell turns orange when this comparison metric is flagged. */
  metric?: ComparisonMetric;
  cell: (outcome: IntegrityOutcome) => string;
}
export function TradingInformationTable(t0) {
  const $ = _c(42);
  const {
    pubkey,
    outcomes,
    winningOutcomeIndex,
    comparisons,
    finalWindowSeconds
  } = t0;
  const t = useTranslations("market.resolutionIntegrity");
  const intl = useFormatter();
  const rows = useTableRows(finalWindowSeconds);
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== comparisons || $[1] !== intl || $[2] !== outcomes || $[3] !== pubkey || $[4] !== rows || $[5] !== t || $[6] !== winningOutcomeIndex) {
    const columns = winnerFirst(outcomes, winningOutcomeIndex);
    let flagged;
    if ($[15] !== comparisons) {
      flagged = new Set();
      for (const comparison of comparisons) {
        if (comparison.flagged) {
          flagged.add(comparison.metric);
        }
      }
      $[15] = comparisons;
      $[16] = flagged;
    } else {
      flagged = $[16];
    }
    t5 = "flex min-w-0 flex-col gap-2";
    let t7;
    if ($[17] !== t) {
      t7 = t("tableTitle");
      $[17] = t;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    if ($[19] !== t7) {
      t6 = <h3 className="text-body-lg font-semibold text-foreground-primary">{t7}</h3>;
      $[19] = t7;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    T1 = Table;
    t2 = "trading-information-table";
    t3 = "min-w-[34rem] border-separate border-spacing-0";
    let t8;
    if ($[21] !== t) {
      t8 = t("tableMetric");
      $[21] = t;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    let t9;
    if ($[23] !== t8) {
      t9 = <TableHead className="w-40 min-w-32 text-caption font-normal text-foreground-tertiary">{t8}</TableHead>;
      $[23] = t8;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    let t10;
    if ($[25] !== intl || $[26] !== pubkey || $[27] !== winningOutcomeIndex) {
      t10 = (outcome, position) => <TableHead key={outcome.index} className={cn("min-w-28 rounded-t-card px-2 py-3 text-center align-top", outcome.index === winningOutcomeIndex && "bg-background-tertiary/60")}><div className="flex flex-col items-center gap-2"><OutcomeThumb marketPubkey={pubkey} index={outcome.index} imageUrl={outcome.imageUrl} imageNsfw={outcome.imageNsfw} size="size-10" showSettledBadge={outcome.index === winningOutcomeIndex} rankLabel={intl.number(position + 1)} /><span className="break-words text-caption font-medium text-foreground-primary">{outcome.label}</span></div></TableHead>;
      $[25] = intl;
      $[26] = pubkey;
      $[27] = winningOutcomeIndex;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    t4 = <TableHeader><TableRow className="border-0">{t9}{columns.map(t10)}</TableRow></TableHeader>;
    T0 = TableBody;
    t1 = rows.map((row, rowIndex) => <TableRow key={row.key} className="border-0"><TableCell className="border-t border-border-secondary py-3 pr-2"><span className="block text-caption text-foreground-secondary">{row.label}</span>{row.hint === undefined ? null : <span className="block text-caption text-foreground-tertiary">{row.hint}</span>}</TableCell>{columns.map(outcome_0 => {
        const isWinner = outcome_0.index === winningOutcomeIndex;
        const isFlagged = isWinner && row.metric !== undefined && flagged.has(row.metric);
        const isLopsided = isWinner && row.metric === "largestHolderShare" && isLopsidedShare(outcome_0.largestHolderSharePpb);
        return <TableCell key={outcome_0.index} data-flagged={isFlagged ? "true" : undefined} data-lopsided={isLopsided ? "true" : undefined} className={cn("border-t border-border-secondary px-2 py-3 text-center tabular-nums", isWinner && "bg-background-tertiary/60", isWinner && rowIndex === rows.length - 1 && "rounded-b-card", isFlagged ? "text-feedback-warning-content" : "text-foreground-primary")}><span className="inline-flex items-center justify-center gap-1">{(isFlagged || isLopsided) && row.metric !== undefined ? <WarningMarker metric={row.metric} flagged={isFlagged} /> : null}{row.cell(outcome_0)}</span></TableCell>;
      })}</TableRow>);
    $[0] = comparisons;
    $[1] = intl;
    $[2] = outcomes;
    $[3] = pubkey;
    $[4] = rows;
    $[5] = t;
    $[6] = winningOutcomeIndex;
    $[7] = T0;
    $[8] = T1;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    T0 = $[7];
    T1 = $[8];
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
  }
  let t7;
  if ($[29] !== T0 || $[30] !== t1) {
    t7 = <T0>{t1}</T0>;
    $[29] = T0;
    $[30] = t1;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  let t8;
  if ($[32] !== T1 || $[33] !== t2 || $[34] !== t3 || $[35] !== t4 || $[36] !== t7) {
    t8 = <T1 data-testid={t2} className={t3}>{t4}{t7}</T1>;
    $[32] = T1;
    $[33] = t2;
    $[34] = t3;
    $[35] = t4;
    $[36] = t7;
    $[37] = t8;
  } else {
    t8 = $[37];
  }
  let t9;
  if ($[38] !== t5 || $[39] !== t6 || $[40] !== t8) {
    t9 = <div className={t5}>{t6}{t8}</div>;
    $[38] = t5;
    $[39] = t6;
    $[40] = t8;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  return t9;
}
function winnerFirst(outcomes: ReadonlyArray<IntegrityOutcome>, winningOutcomeIndex: number): IntegrityOutcome[] {
  return [...outcomes].sort((a, b) => {
    if (a.index === winningOutcomeIndex) return -1;
    if (b.index === winningOutcomeIndex) return 1;
    return a.index - b.index;
  });
}
function useTableRows(finalWindowSeconds) {
  const $ = _c(93);
  const t = useTranslations("market.resolutionIntegrity");
  const intl = useFormatter();
  const format = useMetricFormat();
  let t0;
  if ($[0] !== t) {
    t0 = t("hintOfHoldings");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const ofHoldings = t0;
  let t1;
  if ($[2] !== t) {
    t1 = value => o => o.holders === 0 ? t("noHolders") : value(o);
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const holderCell = t1;
  let t2;
  if ($[4] !== t) {
    t2 = t("rowFinalShare");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== format) {
    t3 = o_0 => format.percent(o_0.finalConsensusPpb);
    $[6] = format;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = {
      key: "finalShare",
      label: t2,
      cell: t3
    };
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t) {
    t5 = t("rowAverageTrade");
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== format) {
    t6 = o_1 => format.d6(o_1.averageTradeD6);
    $[13] = format;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t5 || $[16] !== t6) {
    t7 = {
      key: "averageTrade",
      label: t5,
      cell: t6
    };
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t) {
    t8 = t("rowTraders");
    $[18] = t;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== format) {
    t9 = o_2 => format.count(o_2.traders);
    $[20] = format;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t8 || $[23] !== t9) {
    t10 = {
      key: "traders",
      label: t8,
      cell: t9
    };
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t) {
    t11 = t("rowVolume");
    $[25] = t;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== format) {
    t12 = o_3 => format.d6(o_3.volumeD6);
    $[27] = format;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t11 || $[30] !== t12) {
    t13 = {
      key: "volume",
      label: t11,
      metric: "volume",
      cell: t12
    };
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t) {
    t14 = t("rowHolders");
    $[32] = t;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== format) {
    t15 = o_4 => format.count(o_4.holders);
    $[34] = format;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t14 || $[37] !== t15) {
    t16 = {
      key: "holders",
      label: t14,
      metric: "holders",
      cell: t15
    };
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t) {
    t17 = t("rowAveragePosition");
    $[39] = t;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== format) {
    t18 = o_5 => format.d6(o_5.averagePositionD6);
    $[41] = format;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== holderCell || $[44] !== t18) {
    t19 = holderCell(t18);
    $[43] = holderCell;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t17 || $[47] !== t19) {
    t20 = {
      key: "averagePosition",
      label: t17,
      metric: "averagePosition",
      cell: t19
    };
    $[46] = t17;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== t) {
    t21 = t("rowLargestHolder");
    $[49] = t;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== format) {
    t22 = o_6 => format.percent(o_6.largestHolderSharePpb);
    $[51] = format;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== holderCell || $[54] !== t22) {
    t23 = holderCell(t22);
    $[53] = holderCell;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== ofHoldings || $[57] !== t21 || $[58] !== t23) {
    t24 = {
      key: "largestHolder",
      label: t21,
      hint: ofHoldings,
      metric: "largestHolderShare",
      cell: t23
    };
    $[56] = ofHoldings;
    $[57] = t21;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== t) {
    t25 = t("rowTop5Holders");
    $[60] = t;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== format) {
    t26 = o_7 => format.percent(o_7.top5HoldersSharePpb);
    $[62] = format;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== holderCell || $[65] !== t26) {
    t27 = holderCell(t26);
    $[64] = holderCell;
    $[65] = t26;
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  let t28;
  if ($[67] !== ofHoldings || $[68] !== t25 || $[69] !== t27) {
    t28 = {
      key: "top5Holders",
      label: t25,
      hint: ofHoldings,
      metric: "top5HoldersShare",
      cell: t27
    };
    $[67] = ofHoldings;
    $[68] = t25;
    $[69] = t27;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== t) {
    t29 = t("rowFinalHour");
    $[71] = t;
    $[72] = t29;
  } else {
    t29 = $[72];
  }
  let t30;
  if ($[73] !== finalWindowSeconds || $[74] !== intl || $[75] !== t) {
    t30 = t("hintLateWindow", {
      window: intl.number(...durationUnit(finalWindowSeconds))
    });
    $[73] = finalWindowSeconds;
    $[74] = intl;
    $[75] = t;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  let t31;
  if ($[77] !== format) {
    t31 = o_8 => format.percent(o_8.finalHourNetInflowPpb, true);
    $[77] = format;
    $[78] = t31;
  } else {
    t31 = $[78];
  }
  let t32;
  if ($[79] !== t29 || $[80] !== t30 || $[81] !== t31) {
    t32 = {
      key: "finalHour",
      label: t29,
      hint: t30,
      metric: "finalHourNetInflow",
      cell: t31
    };
    $[79] = t29;
    $[80] = t30;
    $[81] = t31;
    $[82] = t32;
  } else {
    t32 = $[82];
  }
  let t33;
  if ($[83] !== t10 || $[84] !== t13 || $[85] !== t16 || $[86] !== t20 || $[87] !== t24 || $[88] !== t28 || $[89] !== t32 || $[90] !== t4 || $[91] !== t7) {
    t33 = [t4, t7, t10, t13, t16, t20, t24, t28, t32];
    $[83] = t10;
    $[84] = t13;
    $[85] = t16;
    $[86] = t20;
    $[87] = t24;
    $[88] = t28;
    $[89] = t32;
    $[90] = t4;
    $[91] = t7;
    $[92] = t33;
  } else {
    t33 = $[92];
  }
  return t33;
}